import React, { useEffect, useRef, useState } from "react";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  ChevronLeft,
  ChevronRight,
  Calendar,
  Pencil,
  Plus,
  Sparkles,
  Trash2,
  Upload,
  X,
  PlusCircle
} from "lucide-react";
import { useForm } from "react-hook-form";
import { z } from "zod";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";

import PageHeader from "../components/common/PageHeader";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { FieldError, FieldLabel } from "../components/ui/field";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import useToast from "../hooks/useToast";
import { uploadFileToS3 } from "../services/uploadService";
import {
  createEvent,
  deleteEvent,
  getEvents,
  updateEvent
} from "../services/eventsService";

const eventSchema = z.object({
  title: z.string().trim().min(1, "Title is required"),
  image: z.string().trim().optional(),
  date: z.string().trim().optional().nullable(),
  start_time: z.string().trim().optional().nullable(),
  end_time: z.string().trim().optional().nullable(),
  detail_image: z.string().trim().optional(),
  description: z.string().trim().optional(),
  location: z.string().trim().optional()
});

const defaultValues = {
  title: "",
  image: "",
  date: "",
  start_time: "",
  end_time: "",
  detail_image: "",
  description: "",
  location: ""
};

function getVisiblePageNumbers(currentPage, totalPages) {
  if (totalPages <= 7) return Array.from({ length: totalPages }, (_, index) => index + 1);
  if (currentPage <= 4) return [1, 2, 3, 4, 5, "...", totalPages];
  if (currentPage >= totalPages - 3) return [1, "...", totalPages - 4, totalPages - 3, totalPages - 2, totalPages - 1, totalPages];
  return [1, "...", currentPage - 1, currentPage, currentPage + 1, "...", totalPages];
}

export default function Events() {
  const [events, setEvents] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isUploadingImage, setIsUploadingImage] = useState(false);
  const [isUploadingDetail, setIsUploadingDetail] = useState(false);
  const [isDeletingEvent, setIsDeletingEvent] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingEventId, setEditingEventId] = useState(null);
  const [eventPendingDelete, setEventPendingDelete] = useState(null);
  const [pagination, setPagination] = useState({ page: 1, limit: 10, total: 0, totalPages: 1 });
  
  // Custom state for gallery
  const [galleryItems, setGalleryItems] = useState([]);
  const [uploadingGalleryIndex, setUploadingGalleryIndex] = useState(null);

  const fileInputImageRef = useRef(null);
  const fileInputDetailRef = useRef(null);
  const { showErrorToast, showSuccessToast } = useToast();

  const {
    register,
    handleSubmit,
    reset,
    setValue,
    watch,
    formState: { errors, isSubmitting }
  } = useForm({
    defaultValues,
    resolver: zodResolver(eventSchema)
  });

  const mainImage = watch("image");
  const detailImage = watch("detail_image");
  const descriptionContent = watch("description");

  const refreshListing = (targetPage = pagination.page, targetLimit = pagination.limit) => {
    if (targetPage !== pagination.page || targetLimit !== pagination.limit) {
      setPagination((current) => ({ ...current, page: targetPage, limit: targetLimit }));
      return;
    }
    loadEvents(targetPage, targetLimit);
  };

  async function loadEvents(pageArg = pagination.page, limitArg = pagination.limit) {
    setIsLoading(true);
    const response = await getEvents({ page: pageArg, limit: limitArg });
    if (response?.success) {
      setEvents(response.data.events || []);
      setPagination({
        page: response.data.pagination?.page || pageArg,
        limit: response.data.pagination?.limit || limitArg,
        total: response.data.pagination?.total || 0,
        totalPages: response.data.pagination?.totalPages || 1
      });
    } else {
      showErrorToast(response?.message || "Unable to load events");
    }
    setIsLoading(false);
  }

  useEffect(() => {
    loadEvents(pagination.page, pagination.limit);
  }, [pagination.page, pagination.limit]);

  const openCreateModal = () => {
    reset(defaultValues);
    setGalleryItems([]);
    setEditingEventId(null);
    setIsModalOpen(true);
  };

  const closeModal = () => {
    reset(defaultValues);
    setGalleryItems([]);
    setEditingEventId(null);
    setIsModalOpen(false);
  };

  const openDeleteModal = (eventItem) => {
    setEventPendingDelete(eventItem);
  };

  const closeDeleteModal = () => {
    if (isDeletingEvent) return;
    setEventPendingDelete(null);
  };

  const handleFileUpload = (file, type, index = null) => {
    if (!file) return;

    if (!file.type.startsWith("image/")) {
      showErrorToast("Please upload an image file");
      return;
    }

    if (type === "main") setIsUploadingImage(true);
    else if (type === "detail") setIsUploadingDetail(true);
    else if (type === "gallery" && index !== null) setUploadingGalleryIndex(index);

    uploadFileToS3(file, "events").then((response) => {
      if (!response?.success) {
        showErrorToast(response?.message || "Unable to upload image");
        if (type === "main") setIsUploadingImage(false);
        else if (type === "detail") setIsUploadingDetail(false);
        else if (type === "gallery") setUploadingGalleryIndex(null);
        return;
      }

      if (type === "main") {
        setValue("image", response.data.file.url, { shouldDirty: true, shouldValidate: true });
        setIsUploadingImage(false);
      } else if (type === "detail") {
        setValue("detail_image", response.data.file.url, { shouldDirty: true, shouldValidate: true });
        setIsUploadingDetail(false);
      } else if (type === "gallery" && index !== null) {
        setGalleryItems((prev) =>
          prev.map((item, i) => (i === index ? { ...item, image: response.data.file.url } : item))
        );
        setUploadingGalleryIndex(null);
      }
      showSuccessToast("Image uploaded successfully");
    });
  };

  const handleEdit = (eventItem) => {
    setEditingEventId(eventItem.id);
    reset({
      title: eventItem.title,
      image: eventItem.image || "",
      date: eventItem.date || "",
      start_time: eventItem.start_time || "",
      end_time: eventItem.end_time || "",
      detail_image: eventItem.detail_image || "",
      description: eventItem.description || "",
      location: eventItem.location || ""
    });
    setGalleryItems(eventItem.gallery || []);
    setIsModalOpen(true);
  };

  const handleDelete = async () => {
    if (!eventPendingDelete) return;
    setIsDeletingEvent(true);
    const response = await deleteEvent(eventPendingDelete.id);
    setIsDeletingEvent(false);

    if (!response?.success) {
      showErrorToast(response?.message || "Unable to delete event");
      return;
    }

    const nextPage = events.length === 1 && pagination.page > 1 ? pagination.page - 1 : pagination.page;
    showSuccessToast(response.message || "Event deleted successfully");
    closeDeleteModal();
    refreshListing(nextPage, pagination.limit);
  };

  const onSubmit = async (values) => {
    const payload = {
      ...values,
      gallery: galleryItems
    };

    const response = editingEventId
      ? await updateEvent(editingEventId, payload)
      : await createEvent(payload);

    if (!response?.success) {
      showErrorToast(response?.message || "Unable to save event");
      return;
    }

    showSuccessToast(response.message || "Event saved successfully");
    closeModal();
    if (editingEventId) refreshListing(pagination.page, pagination.limit);
    else refreshListing(1, pagination.limit);
  };

  // Gallery Helpers
  const addGalleryItem = () => {
    setGalleryItems([...galleryItems, { title: "", image: "" }]);
  };

  const removeGalleryItem = (index) => {
    setGalleryItems(galleryItems.filter((_, i) => i !== index));
  };

  const updateGalleryItem = (index, field, value) => {
    setGalleryItems(
      galleryItems.map((item, i) => (i === index ? { ...item, [field]: value } : item))
    );
  };

  const startItem = pagination.total ? (pagination.page - 1) * pagination.limit + 1 : 0;
  const endItem = pagination.total ? Math.min(pagination.page * pagination.limit, pagination.total) : 0;
  const pageNumbers = getVisiblePageNumbers(pagination.page, pagination.totalPages);

  return (
    <div className="space-y-6">
      <PageHeader
        eyebrow="Events Module"
        title="Events"
        description="Manage events, dates, locations, and interactive gallery displays."
        badge={`${pagination.total} Events`}
      />

      <Card className="border-white/90 bg-white/84">
        <CardHeader className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
          <div>
            <CardTitle>Event Listing</CardTitle>
            <p className="mt-2 text-sm text-panel-muted">View and manage entries with paginated control.</p>
          </div>
          <div className="flex items-center gap-3">
            <Button type="button" onClick={openCreateModal}>
              <Plus className="mr-2 h-4 w-4" /> Add Event
            </Button>
          </div>
        </CardHeader>
        <CardContent className="space-y-5">
          {isLoading ? (
            <div className="rounded-3xl border p-6 text-sm text-panel-muted">Loading events...</div>
          ) : events.length ? (
            <>
              <div className="overflow-hidden rounded-3xl border border-slate-100">
                <div className="overflow-x-auto">
                  <table className="min-w-full border-collapse">
                    <thead className="bg-slate-50/90">
                      <tr className="text-left">
                        <th className="px-5 py-4 text-xs font-semibold uppercase tracking-[0.2em] text-panel-muted">Title</th>
                        <th className="px-5 py-4 text-xs font-semibold uppercase tracking-[0.2em] text-panel-muted">Date</th>
                        <th className="px-5 py-4 text-xs font-semibold uppercase tracking-[0.2em] text-panel-muted">Location</th>
                        <th className="px-5 py-4 text-xs font-semibold uppercase tracking-[0.2em] text-panel-muted">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white">
                      {events.map((item) => (
                        <tr key={item.id} className="border-t border-slate-100 align-top">
                          <td className="px-5 py-4 text-sm font-semibold text-panel-ink">{item.title}</td>
                          <td className="px-5 py-4 text-sm text-panel-muted">{item.date || "--"}</td>
                          <td className="px-5 py-4 text-sm text-panel-muted">{item.location || "--"}</td>
                          <td className="px-5 py-4">
                            <div className="flex gap-2">
                              <Button type="button" size="sm" variant="outline" onClick={() => handleEdit(item)}>
                                <Pencil className="mr-2 h-4 w-4" /> Edit
                              </Button>
                              <Button type="button" size="sm" variant="ghost" className="hover:bg-rose-50 hover:text-rose-600 text-panel-muted" onClick={() => openDeleteModal(item)}>
                                <Trash2 className="mr-2 h-4 w-4" /> Delete
                              </Button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              <div className="flex flex-col gap-4 rounded-3xl border border-slate-100 bg-slate-50/70 px-4 py-4 md:flex-row md:items-center md:justify-between">
                <p className="text-sm text-panel-muted">
                  Showing <span className="font-semibold text-panel-ink">{startItem}</span> to <span className="font-semibold text-panel-ink">{endItem}</span> of <span className="font-semibold text-panel-ink">{pagination.total}</span> events
                </p>
                <div className="flex flex-wrap items-center gap-2">
                  <Button type="button" variant="outline" size="sm" disabled={pagination.page <= 1} onClick={() => setPagination((c) => ({ ...c, page: c.page - 1 }))}>Prev</Button>
                  {pageNumbers.map((p, i) => (
                    p === "..." ? <span key={i} className="px-1 text-sm">...</span> : 
                    <Button key={i} type="button" size="sm" variant={pagination.page === p ? "default" : "outline"} onClick={() => setPagination((c) => ({ ...c, page: p }))}>{p}</Button>
                  ))}
                  <Button type="button" variant="outline" size="sm" disabled={pagination.page >= pagination.totalPages} onClick={() => setPagination((c) => ({ ...c, page: c.page + 1 }))}>Next</Button>
                </div>
              </div>
            </>
          ) : (
            <div className="rounded-3xl border border-dashed border-slate-200 p-8 text-center">No events found.</div>
          )}
        </CardContent>
      </Card>

      {isModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/45 p-4 overflow-y-auto">
          <div className="max-h-[90dvh] w-full max-w-4xl overflow-y-auto rounded-[32px] border bg-white shadow-xl p-6">
            <div className="flex items-center justify-between border-b pb-4 mb-4">
              <h2 className="text-2xl font-bold">{editingEventId ? "Edit Event" : "Add Event"}</h2>
              <button type="button" onClick={closeModal}><X /></button>
            </div>

            <form className="space-y-4" onSubmit={handleSubmit(onSubmit)}>
              <div className="space-y-2">
                <FieldLabel>Title *</FieldLabel>
                <Input placeholder="Enter event title" {...register("title")} />
                {errors.title && <FieldError>{errors.title.message}</FieldError>}
              </div>

              <div className="grid grid-cols-3 gap-4">
                <div className="space-y-2">
                  <FieldLabel>Date</FieldLabel>
                  <Input type="date" {...register("date")} />
                </div>
                <div className="space-y-2">
                  <FieldLabel>Start Time</FieldLabel>
                  <Input type="time" {...register("start_time")} />
                </div>
                <div className="space-y-2">
                  <FieldLabel>End Time</FieldLabel>
                  <Input type="time" {...register("end_time")} />
                </div>
              </div>

              <div className="space-y-2">
                <FieldLabel>Location</FieldLabel>
                <Input placeholder="Enter location" {...register("location")} />
              </div>

              <div className="space-y-2 event-editor-wrapper">
                <style>{`
                  .event-editor-wrapper .ql-container { height: 160px; overflow-y: auto; }
                  .event-editor-wrapper .ql-editor { min-height: 160px; }
                `}</style>
                <FieldLabel>Description (Rich Text)</FieldLabel>
                <ReactQuill theme="snow" value={descriptionContent || ""} onChange={(content) => setValue("description", content)} />
              </div>

              <div className="grid grid-cols-2 gap-4 mt-6">
                <div className="space-y-2 border p-4 rounded-xl">
                  <FieldLabel>Main Image</FieldLabel>
                  <input ref={fileInputImageRef} type="file" accept="image/*" className="hidden" onChange={(e) => handleFileUpload(e.target.files?.[0], "main")} />
                  <Button type="button" variant="outline" onClick={() => fileInputImageRef.current?.click()} disabled={isUploadingImage}>
                    {isUploadingImage ? "Uploading..." : "Upload Main Image"}
                  </Button>
                  {mainImage && <img src={mainImage} alt="Main" className="h-20 w-20 object-cover mt-2 rounded-lg" />}
                </div>

                <div className="space-y-2 border p-4 rounded-xl">
                  <FieldLabel>Detail Image</FieldLabel>
                  <input ref={fileInputDetailRef} type="file" accept="image/*" className="hidden" onChange={(e) => handleFileUpload(e.target.files?.[0], "detail")} />
                  <Button type="button" variant="outline" onClick={() => fileInputDetailRef.current?.click()} disabled={isUploadingDetail}>
                    {isUploadingDetail ? "Uploading..." : "Upload Detail Image"}
                  </Button>
                  {detailImage && <img src={detailImage} alt="Detail" className="h-20 w-20 object-cover mt-2 rounded-lg" />}
                </div>
              </div>

              {/* Dynamic Gallery Section */}
              <div className="space-y-4 border p-4 rounded-xl bg-slate-50">
                <div className="flex items-center justify-between">
                  <FieldLabel className="text-base font-semibold">Event Gallery</FieldLabel>
                  <Button type="button" variant="outline" size="sm" onClick={addGalleryItem}>
                    <PlusCircle className="mr-1 h-4 w-4" /> Add Item
                  </Button>
                </div>

                {galleryItems.map((gItem, index) => (
                  <div key={index} className="grid grid-cols-[1fr_200px_40px] gap-3 bg-white p-3 rounded-xl border">
                    <div className="space-y-1">
                      <label className="text-xs font-semibold text-panel-muted">Item Title</label>
                      <Input
                        placeholder="Gallery item title"
                        value={gItem.title}
                        onChange={(e) => updateGalleryItem(index, "title", e.target.value)}
                      />
                    </div>
                    <div className="space-y-1">
                      <label className="text-xs font-semibold text-panel-muted">Image</label>
                      <div className="flex items-center gap-2">
                        <input
                          type="file"
                          accept="image/*"
                          className="hidden"
                          id={`gallery-upload-${index}`}
                          onChange={(e) => handleFileUpload(e.target.files?.[0], "gallery", index)}
                        />
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={() => document.getElementById(`gallery-upload-${index}`).click()}
                          disabled={uploadingGalleryIndex === index}
                        >
                          {uploadingGalleryIndex === index ? "Uploading..." : "Upload"}
                        </Button>
                        {gItem.image && <img src={gItem.image} alt="Gallery" className="h-8 w-8 object-cover rounded" />}
                      </div>
                    </div>
                    <div className="flex items-end justify-center">
                      <Button type="button" variant="ghost" size="sm" className="text-rose-500 hover:text-rose-600" onClick={() => removeGalleryItem(index)}>
                        <X size={16} />
                      </Button>
                    </div>
                  </div>
                ))}

                {galleryItems.length === 0 && (
                  <p className="text-xs text-center text-panel-muted">No gallery items added yet.</p>
                )}
              </div>

              <div className="flex gap-3 pt-4 border-t">
                <Button type="submit" disabled={isSubmitting || isUploadingImage || isUploadingDetail || uploadingGalleryIndex !== null}>
                  {isSubmitting ? "Saving..." : editingEventId ? "Update Event" : "Save Event"}
                </Button>
                <Button type="button" variant="outline" onClick={closeModal}>Cancel</Button>
              </div>
            </form>
          </div>
        </div>
      )}

      {eventPendingDelete && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-slate-950/50 p-4">
          <div className="w-full max-w-md rounded-[32px] border bg-white p-6 shadow-xl">
            <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-rose-50 text-rose-600">
              <Trash2 className="h-5 w-5" />
            </div>
            <h2 className="mt-5 text-2xl font-bold">Delete Event</h2>
            <p className="mt-3 text-sm text-panel-muted">
              Are you sure you want to delete <span className="font-semibold">{eventPendingDelete.title}</span>?
            </p>
            <div className="mt-6 flex gap-3">
              <Button type="button" variant="destructive" onClick={handleDelete} disabled={isDeletingEvent}>
                {isDeletingEvent ? "Deleting..." : "Delete Event"}
              </Button>
              <Button type="button" variant="outline" onClick={closeDeleteModal} disabled={isDeletingEvent}>
                Cancel
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
